import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Checkbox,
  Typography,
  Alert,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { api } from '../../api/chat';
import {
  refreshConversations,
  setActiveConversation,
  loadMessages,
  setRightContent,
} from '../../redux/chat.redux';
import { avatarText } from '../../utils/format';
import PanelShell from './panel-shell';

interface Props {
  onClose: () => void;
  embedded?: boolean;
}

export default function NewGroupPanel({ onClose, embedded }: Props) {
  const dispatch = useAppDispatch();
  const contacts = useAppSelector((s) => s.chat.contacts);
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');

  const toggle = (id: number) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const create = async () => {
    if (!name.trim()) {
      setErr('请输入群名称');
      return;
    }
    if (selected.length === 0) {
      setErr('请至少选择一位好友');
      return;
    }
    setCreating(true);
    setErr('');
    try {
      const conv = await api.createGroup(name.trim(), selected);
      dispatch(refreshConversations());
      dispatch(setActiveConversation(conv.id));
      dispatch(loadMessages(conv.id));
      // 创建成功后右侧直接显示该群聊详情(不调用 onClose,避免被重置为空态)
      dispatch(setRightContent({ type: 'group-detail', groupId: conv.id }));
    } catch (err: any) {
      setErr(err?.message || '创建失败');
    } finally {
      setCreating(false);
    }
  };

  return (
    <PanelShell title="创建群聊" onClose={onClose} embedded={embedded}>
      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="群名称"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />
        <Typography variant="caption" color="text.secondary">
          选择群成员({selected.length}/{contacts.length})
        </Typography>
        {contacts.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
            暂无好友,请先添加好友
          </Typography>
        )}
        <List disablePadding sx={{ maxHeight: 320, overflowY: 'auto', bgcolor: '#fff', borderRadius: 2, mt: 1 }}>
          {contacts.map((u) => (
            <ListItem key={u.id} disablePadding sx={{ px: 1 }}>
              <Checkbox checked={selected.includes(u.id)} onChange={() => toggle(u.id)} size="small" />
              <ListItemAvatar>
                <Avatar src={u.avatar || undefined}>{avatarText(u)}</Avatar>
              </ListItemAvatar>
              <ListItemText primary={u.nickname || u.username} />
            </ListItem>
          ))}
        </List>
        {err && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {err}
          </Alert>
        )}
        <Button fullWidth variant="contained" onClick={create} disabled={creating} sx={{ mt: 2, borderRadius: 2 }}>
          {creating ? '创建中…' : '创建群聊'}
        </Button>
      </Box>
    </PanelShell>
  );
}
